import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from './images/arrow-down.svg';

const useStyles = makeStyles(theme => ({
  root: {
    ...theme.typography.body2,
    color: theme.palette.text.primary,
    marginTop: '16px',
    maxWidth: '300px',
    position: 'relative',
  },

  selectButton: {
    ...theme.typography.body2,
    color: theme.palette.text.primary,
    appearance: 'none',
    outline: 'none',
    textAlign: 'left',
    padding: '20px 16px',
    border: `1px solid ${theme.palette.bgColor.default}`,
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    position: 'relative',
    width: '100%',
    backgroundColor: 'white',
    minHeight: '64px',
  },

  selectButtonOpen: {
    borderBottomLeftRadius: '0',
    borderBottomRightRadius: '0',
    borderBottom: 'none',
    border: `3px solid ${theme.palette.bgColor.default}`,
  },

  selectText: {
    ...theme.typography.body2,
    color: theme.palette.text.primary,
    flex: 1,
  },

  arrow: {
    pointerEvents: 'none',
    transition: 'transform 0.2s ease',
  },

  arrowOpen: {
    transform: 'rotate(180deg)',
  },

  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: 'white',
    border: `3px solid ${theme.palette.bgColor.default}`,
    borderBottomLeftRadius: '16px',
    borderBottomRightRadius: '16px',
    maxHeight: '496px',
    overflowY: 'auto',
    zIndex: 1000,
  },

  option: {
    ...theme.typography.body2,
    color: theme.palette.text.primary,
    padding: '19px 16px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#D9FBFF',
    },
  },

  optionSelected: {
    backgroundColor: '#D9FBFF',
    fontWeight: 600,
  },

  // Hide the original select element
  hiddenSelect: {
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
    zIndex: -1,
  },
}));

/**
 * CustomSelect for Blue Shield
 *
 * Props:
 * - value, onChange, options, placeholder, name: as before
 * - ArrowComponent: optional, a React component to render as the dropdown arrow
 */
const CustomSelect = ({
  value,
  onChange,
  options,
  placeholder,
  name,
  ArrowComponent,
  ...props
}) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get the display text for the current value
  const getDisplayText = () => {
    if (!value) return placeholder || '';
    const selectedOption = options.find(opt => (opt.value || opt) === value);
    return selectedOption ? selectedOption.text || selectedOption : value;
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = optionValue => {
    // Create a synthetic event to match the expected onChange signature
    const syntheticEvent = {
      target: {
        name,
        value: optionValue,
      },
    };
    onChange(syntheticEvent);
    setIsOpen(false);
  };

  return (
    <div className={classes.root} ref={dropdownRef}>
      {/* Hidden select for form submission */}
      <select
        className={classes.hiddenSelect}
        value={value}
        onChange={onChange}
        name={name}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map(opt => (
          <option key={opt.value || opt} value={opt.value || opt}>
            {opt.text || opt}
          </option>
        ))}
      </select>

      {/* Custom dropdown button */}
      <div
        className={`${classes.selectButton} ${
          isOpen ? classes.selectButtonOpen : ''
        }`}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
      >
        <span className={classes.selectText}>{getDisplayText()}</span>
        {ArrowComponent ? (
          <ArrowComponent
            className={`${classes.arrow} ${isOpen ? classes.arrowOpen : ''}`}
          />
        ) : (
          <img
            src={ArrowDropDownIcon}
            alt=""
            className={`${classes.arrow} ${isOpen ? classes.arrowOpen : ''}`}
          />
        )}
      </div>

      {/* Dropdown list */}
      {isOpen && (
        <div className={classes.dropdown}>
          {options.map(opt => {
            const optionValue = opt.value || opt;
            const optionText = opt.text || opt;
            const isSelected = optionValue === value;

            return (
              <div
                key={optionValue}
                className={`${classes.option} ${
                  isSelected ? classes.optionSelected : ''
                }`}
                onClick={() => handleOptionClick(optionValue)}
                role="option"
                tabIndex={0}
                aria-selected={isSelected}
              >
                {optionText}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
